import { useQuery } from '@tanstack/react-query';
import { httpService } from '@services/httpService';
import { ApiRoute } from '@routes/apiRoutes';
import { QueryKeys } from '@enums/queryKeys';
import { LoanProgressModel } from '@models/drawRequests/loanProgressModel';
import { DrawRequest } from '@models/drawRequests/drawRequest';
import { BudgetItemResponse } from '@models/drawRequests/budgetItemResponse';
import { TableResponseModel } from '@models/table/tableResponseModel';
import { DrawRequestWithDetails } from '@models/drawRequests/drawRequestWithDetails';
import { DrawRequestWithDetailsResponseModel } from '@models/drawRequests/drawRequestWithDetailsResponseModel';
import { BaseRequestModel } from '@models/drawRequests/baseRequestModel';
import { DrawRequestIdModel } from '@models/drawRequests/drawRequestIdModel';
import { DrawRequestTableRequestModel } from '@models/drawRequests/drawRequestTableRequestModel';

export const useDrawRequestQueryApi = {
  useFetchLoanProgress: (sourceRecId: string) => {
    const getLoanProgress = async () => {
      const request: BaseRequestModel = {
        loanRecId: sourceRecId,
        sourceRecId,
      };
      return await httpService.post<LoanProgressModel>(
        ApiRoute.ConstructionDrawRequestLoanProgress,
        request
      );
    };

    return useQuery<LoanProgressModel | null, Error>({
      queryKey: [QueryKeys.LOAN_PROGRESS, sourceRecId],
      queryFn: () => getLoanProgress(),
      enabled: !!sourceRecId,
    });
  },

  useFetchIsDrawRequestFeatureEnabled: (sourceRecId: string) => {
    const getIsDrawRequestFeatureEnabled = async () => {
      const request: BaseRequestModel = {
        loanRecId: sourceRecId,
        sourceRecId,
      };
      return await httpService.post<boolean>(
        ApiRoute.ConstructionDrawRequestFeatureEnabled,
        request
      );
    };

    return useQuery<boolean | null, Error>({
      queryKey: [
        QueryKeys.CONSTRUCTION_DRAW_REQUEST_FEATURE_ENABLED,
        sourceRecId,
      ],
      queryFn: () => getIsDrawRequestFeatureEnabled(),
      enabled: !!sourceRecId,
    });
  },

  useFetchRequestDrawButtonEnabled: (sourceRecId: string) => {
    const getRequestDrawButtonEnabled = async () => {
      const request: BaseRequestModel = {
        loanRecId: sourceRecId,
        sourceRecId,
      };
      return await httpService.post<boolean>(
        ApiRoute.ConstructionRequestDrawButtonEnabled,
        request
      );
    };

    return useQuery<boolean | null, Error>({
      queryKey: [
        QueryKeys.CONSTRUCTION_REQUEST_DRAW_BUTTON_ENABLED,
        sourceRecId,
      ],
      queryFn: () => getRequestDrawButtonEnabled(),
      enabled: !!sourceRecId,
    });
  },

  useFetchDrawRequests: (
    request: { filters: any; params: any },
    sourceRecId: string
  ) => {
    const getDrawRequests = async () => {
      const tableRequest: DrawRequestTableRequestModel = {
        tableRequest: {
          filters: request.filters,
          params: request.params,
        },
        sourceRecId,
      };
      return await httpService.post<TableResponseModel<DrawRequest>>(
        ApiRoute.ConstructionDrawRequestQuery,
        tableRequest
      );
    };

    return useQuery<TableResponseModel<DrawRequest> | null, Error>({
      queryKey: [
        QueryKeys.DRAW_REQUESTS,
        request.filters?.loanRecId,
        sourceRecId,
        request.params.page,
        request.params.rowsPerPage,
        request.params.order?.name,
        request.params.order?.direction,
      ],
      queryFn: () => getDrawRequests(),
      enabled: !!sourceRecId,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    });
  },

  useFetchBudgetItems: (sourceRecId: string) => {
    const getBudgetItems = async () => {
      const request: BaseRequestModel = {
        loanRecId: sourceRecId,
        sourceRecId,
      };
      return await httpService.post<BudgetItemResponse[]>(
        ApiRoute.ConstructionDrawRequestBudgetedItems,
        request
      );
    };

    return useQuery<BudgetItemResponse[] | null, Error>({
      queryKey: [QueryKeys.BUDGET_ITEMS, sourceRecId],
      queryFn: () => getBudgetItems(),
      enabled: !!sourceRecId,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });
  },

  useFetchDrawRequestDetails: (drawRequestId: string, sourceRecId: string) => {
    const getDrawRequestDetails = async () => {
      const request: DrawRequestIdModel = {
        drawRequestId,
        sourceRecId,
      };
      const response =
        await httpService.post<DrawRequestWithDetailsResponseModel>(
          ApiRoute.ConstructionDrawRequestWithDetails,
          request
        );

      return {
        id: response.id,
        drawRequestId: response.drawRequestId.toString(),
        status: response.status || 0,
        dateCreated: response.dateCreated
          ? new Date(response.dateCreated)
          : new Date(),
        budgetAmount: response.budgetAmount || 0,
        requestAmount: response.requestAmount || 0,
        loanRecId: response.loanRecId || '',
        drawRequestDetails: response.drawRequestDetails?.map(detail => ({
          recId: detail.recId,
          budgetItemRecId: detail.budgetItemRecId,
          remainingBudget: detail.remainingBudget,
          drawAmount: detail.drawAmount,
          drawRequestRecId: drawRequestId,
          pctCompleted: detail.pctCompleted,
        })),
      } as DrawRequestWithDetails;
    };

    return useQuery<DrawRequestWithDetails | null, Error>({
      queryKey: [QueryKeys.DRAW_REQUEST_DETAILS, drawRequestId, sourceRecId],
      queryFn: () => getDrawRequestDetails(),
      enabled: !!drawRequestId && !!sourceRecId,
    });
  },
};

export default useDrawRequestQueryApi;
